import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Search,
  MessageCircle,
  Bug,
  Lightbulb,
  Mail,
  BookOpen,
  ArrowRight,
  CircleDot,
} from "lucide-react";
import { useSupport } from "@/contexts/SupportContext";
import { useAuth } from "@/hooks/useAuth";
import { MyTicketsTab } from "./MyTicketsTab";
import { SampleProjectButton } from "@/components/client/ClientGettingStarted";

interface GuideLink {
  title: string;
  description: string;
  href: string;
  audience?: "all" | "client" | "agency";
}

const GUIDES: GuideLink[] = [
  { title: "How bookings work", description: "Requests, offers, payments, and call sheets.", href: "/guide/bookings" },
  { title: "Packages & shortlists", description: "Browsing talent and building shortlists.", href: "/guide/packages" },
  { title: "Fittings", description: "Scheduling and managing fittings.", href: "/guide/fittings" },
  { title: "Casting briefs", description: "How to write a brief that gets responses.", href: "/casting-guide" },
  { title: "Usage rights basics", description: "What usage terms mean for your campaign.", href: "/usage-basics" },
];

export function HelpCenterSheet() {
  const { helpOpen, closeHelp, openTicket } = useSupport();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const filteredGuides = GUIDES.filter((g) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return g.title.toLowerCase().includes(q) || g.description.toLowerCase().includes(q);
  });

  const goTo = (href: string) => {
    closeHelp();
    navigate(href);
  };

  return (
    <Sheet open={helpOpen} onOpenChange={(o) => (o ? null : closeHelp())}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle className="font-display text-2xl">Help Center</SheetTitle>
          <p className="text-sm text-muted-foreground">
            Search guides, ask Focus.AI, or report a problem.
          </p>
        </SheetHeader>

        <div className="px-6 py-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles, guides…"
              className="pl-9"
              autoFocus
            />
          </div>
        </div>

        <Tabs defaultValue="help" className="flex-1 flex flex-col min-h-0">
          <TabsList className={`mx-6 mt-3 grid ${user ? "grid-cols-2" : "grid-cols-1"}`}>
            <TabsTrigger value="help">Help</TabsTrigger>
            {user && <TabsTrigger value="tickets">My tickets</TabsTrigger>}
          </TabsList>

          <TabsContent value="help" className="flex-1 overflow-y-auto px-6 py-4 space-y-6 m-0">
            {user && user.user_metadata?.user_type !== "agency" && user.user_metadata?.user_type !== "model" && <section className="space-y-2">
              <Button variant="outline" className="w-full justify-between" onClick={() => goTo("/dashboard?tab=overview&gettingStarted=1")}><BookOpen className="h-4 w-4" />Getting started<ArrowRight className="h-4 w-4" /></Button>
              <SampleProjectButton />
            </section>}
            {/* Popular guides */}
            <section>
              <h3 className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                Popular guides
              </h3>
              <ul className="space-y-1">
                {filteredGuides.length === 0 && (
                  <li className="text-sm text-muted-foreground italic px-2 py-3">
                    No guides match "{query}". Try Focus.AI or report below.
                  </li>
                )}
                {filteredGuides.map((g) => (
                  <li key={g.href}>
                    <button
                      onClick={() => goTo(g.href)}
                      className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-accent transition-colors text-left group"
                    >
                      <BookOpen className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{g.title}</p>
                        <p className="text-xs text-muted-foreground">{g.description}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            {/* Get help */}
            <section>
              <h3 className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                Get help
              </h3>
              <div className="grid gap-2">
                <ActionRow
                  icon={<MessageCircle className="h-4 w-4 text-primary" />}
                  title="Ask Focus.AI"
                  subtitle="Instant answers from our assistant."
                  onClick={() => {
                    closeHelp();
                    window.dispatchEvent(new CustomEvent("focus-ai-open"));
                  }}
                />
                <ActionRow
                  icon={<Bug className="h-4 w-4 text-destructive" />}
                  title="Report a bug"
                  subtitle="Something not working? Let us know."
                  onClick={() => openTicket({ type: "bug" })}
                />
                <ActionRow
                  icon={<Lightbulb className="h-4 w-4 text-amber-500" />}
                  title="Suggest a feature"
                  subtitle="Have an idea that would make Focus better?"
                  onClick={() => openTicket({ type: "feature" })}
                />
                <ActionRow
                  icon={<Mail className="h-4 w-4 text-muted-foreground" />}
                  title="Contact support"
                  subtitle="Email info@focuscasting.com"
                  onClick={() => {
                    closeHelp();
                    window.location.href = "mailto:info@focuscasting.com";
                  }}
                />
              </div>
            </section>

            {/* Status */}
            <section className="border-t pt-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CircleDot className="h-3 w-3 text-emerald-500" />
                <span>All systems operational</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Tip: Press <kbd className="px-1.5 py-0.5 rounded bg-muted text-foreground font-mono text-[10px]">?</kbd> anywhere to open this panel.
              </p>
            </section>
          </TabsContent>

          {user && (
            <TabsContent value="tickets" className="flex-1 overflow-y-auto px-6 py-4 m-0">
              <MyTicketsTab />
            </TabsContent>
          )}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function ActionRow({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-start gap-3 px-3 py-3 rounded-lg border border-border/40 hover:border-border hover:bg-accent/40 transition-colors text-left"
    >
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground mt-1" />
    </button>
  );
}

